import {
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from "react";
import { useParams } from "react-router-dom";
import { firestore } from "../../firebase/config";
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  where,
  query as firestoreQuery,
} from "firebase/firestore";
import Loading from "../../components/loading/Loading.jsx";
import Tournament from "./Tournament.jsx";

const TournamentContext = createContext();
const TournamentUpdateContext = createContext();

export const useTournament = () => useContext(TournamentContext);
export const useTournamentUpdate = () => useContext(TournamentUpdateContext);

const TournamentProvider = ({ user }) => {
  const { tournamentId } = useParams();

  const [isUserOrganizer, setIsUserOrganizer] = useState(undefined);
  const [tournament, setTournament] = useState(undefined);
  const [teams, setTeams] = useState(undefined);
  const [matches, setMatches] = useState(undefined);
  const [games, setGames] = useState(undefined);
  const [profiles, setProfiles] = useState(undefined);

  // Fetchers
  const fetchDocData = useCallback(
    async (collectionName, setter, currentState) => {
      if (currentState) return;
      try {
        const ref = doc(firestore, collectionName, tournamentId);
        const snap = await getDoc(ref);
        if (snap.exists()) setter(snap.data());
      } catch (err) {
        console.error(`Error fetching ${collectionName}:`, err);
      }
    },
    [tournamentId]
  );

  const fetchTournament = useCallback(
    () => fetchDocData("tournaments", setTournament, tournament),
    [fetchDocData, tournament]
  );
  const fetchTeams = useCallback(
    () => fetchDocData("teams", setTeams, teams),
    [fetchDocData, teams]
  );
  const fetchMatches = useCallback(
    () => fetchDocData("schedules", setMatches, matches),
    [fetchDocData, matches]
  );
  const fetchGames = useCallback(
    () => fetchDocData("brackets", setGames, games),
    [fetchDocData, games]
  );

  const fetchProfiles = useCallback(async () => {
    if (!teams || !tournament?.name) return;

    // Gather profile IDs
    let allProfileIds = tournament?.playerIds || [];
    if (!tournament?.playerIds) {
      for (const team of Object.values(teams)) {
        if (team?.profileIds?.length) {
          allProfileIds.push(
            ...team.profileIds.filter((id) => id.length === 28)
          );
        }
      }
    }

    if (allProfileIds.length === 0) {
      setProfiles({});
      return;
    }

    // Check session storage
    const cacheKey = `${tournamentId}-profiles`;
    if (tournament.schedule) {
      const cached = sessionStorage.getItem(cacheKey);
      if (cached) {
        setProfiles(JSON.parse(cached));
        return;
      }
    }

    // Fetch from Firestore
    try {
      const profilesRef = collection(firestore, "profiles");
      const q = firestoreQuery(
        profilesRef,
        where(documentId(), "in", allProfileIds)
      );
      const snap = await getDocs(q);

      const profilesData = {};
      snap.forEach((doc) => {
        profilesData[doc.id] = doc.data();
      });

      setProfiles(profilesData);

      if (tournament.schedule) {
        sessionStorage.setItem(cacheKey, JSON.stringify(profilesData));
      }
    } catch (err) {
      console.error("Error fetching profiles:", err);
    }
  }, [teams, tournament, tournamentId]);

  // UseEffects
  useEffect(() => {
    fetchTournament();
    fetchTeams();
    fetchMatches();
    fetchGames();
  }, [fetchTournament, fetchTeams, fetchMatches, fetchGames]);

  useEffect(() => {
    if (!tournament) return;
    setIsUserOrganizer(
      user ? tournament.organizerUids.includes(user.uid) : false
    );
  }, [tournament, user]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Update States
  const updateTournament = (section, object) =>
    setTournament((prev) => ({ ...prev, [section]: object }));

  const updateTeam = (teamId, team) => {
    setTeams((prev) => {
      const updated = { ...prev };
      if (!team) delete updated[teamId];
      else updated[teamId] = team;
      return updated;
    });
  };

  const updateMatches = (matchId, partial) => {
    setMatches((prev) => ({
      ...prev,
      [matchId]: { ...prev[matchId], ...partial },
    }));
  };

  const updateGames = (gameId, partial) => {
    setGames((prev) => ({
      ...prev,
      [gameId]: { ...prev[gameId], ...partial },
    }));
  };

  const updateBracket = (section, object, bracketLevel) => {
    setTournament((prev) => ({
      ...prev,
      [section]: { [bracketLevel]: object },
    }));
  };

  if (
    !tournament ||
    (teams && !profiles) ||
    typeof isUserOrganizer === "undefined" ||
    typeof profiles === "undefined"
  ) {
    return <Loading />;
  }

  return (
    <TournamentContext.Provider
      value={{
        isUserOrganizer,
        tournament,
        tournamentId,
        teams,
        matches,
        games,
        profiles,
      }}
    >
      <TournamentUpdateContext.Provider
        value={{
          updateTournament,
          updateTeam,
          updateMatches,
          updateGames,
          updateBracket,
        }}
      >
        <Tournament user={user} />
      </TournamentUpdateContext.Provider>
    </TournamentContext.Provider>
  );
};

export default TournamentProvider;
